import { AppSettings } from '../app.settings';

/**
 * The shape stored in localStorage under AppSettings.AUTH_TOKEN_NAME.
 *
 * `adopted` marks a session created through the admin ADOPT IDENTITY screen.
 * That token is minted by the legacy PHP API rather than the ecommerce API, so
 * it cannot be validated against ecommerce-api/sms-session - see
 * SmsApiSessionService.getSession, which routes adopted sessions to the legacy
 * session endpoint instead.
 */
export interface AuthPackage {
  username: string;
  token: string;
  adopted?: boolean;
  adopted_by?: string;
}

/**
 * Plain functions rather than an injectable service: SmsApiSessionService is a
 * dependency of AuthService, so anything both need has to sit outside the DI
 * graph to avoid a circular import.
 */
export function readAuthPackage(): AuthPackage | null {
  try {
    const raw = localStorage.getItem(AppSettings.AUTH_TOKEN_NAME);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed as AuthPackage : null;
  } catch {
    // Malformed storage should log the user out, not crash the whole app on boot.
    return null;
  }
}

export function writeAuthPackage(auth_package: AuthPackage): void {
  localStorage.setItem(AppSettings.AUTH_TOKEN_NAME, JSON.stringify(auth_package));
}

export function isAdoptedSession(): boolean {
  const auth_package = readAuthPackage();
  return !!(auth_package && auth_package.adopted);
}

export function adoptedSessionLabel(): string {
  const auth_package = readAuthPackage();
  if (!auth_package || !auth_package.adopted) {
    return '';
  }
  return auth_package.adopted_by
    ? `${auth_package.username} (adopted by ${auth_package.adopted_by})`
    : auth_package.username;
}
